import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Ban, LockKeyhole, PackageCheck, Pencil, Play, Printer, Save, UserX } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, newIdempotencyKey } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatDate, formatNumber } from "../lib/format";
import type { Unit } from "./UnitsPage";

type Allocation = {
  id: string;
  rank_in_batch: number;
  queue_rank: number;
  status: string;
  version: number;
  household_id: string;
  household_code: string;
  head_name: string;
  phone_last4: string | null;
};
type BatchDetails = {
  id: string;
  batch_number: string;
  agent_id: string;
  agent_name: string;
  scope_unit_id: string;
  scope_name: string;
  cycle_id: string | null;
  cycle_name: string | null;
  scheduled_date: string;
  quantity: number;
  notes: string | null;
  allocation_digest: string | null;
  status: string;
  version: number;
  allocations: Allocation[];
};
type AgentOption = { id: string; name: string; status: string };
type CycleOption = { id: string; name_ar: string; status: string };

export function BatchDetailPage() {
  const { id = "" } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deliveryConfirmOpen, setDeliveryConfirmOpen] = useState(false);
  const [deliveryNotice, setDeliveryNotice] = useState<string | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<Allocation | null>(null);
  const [noShowReason, setNoShowReason] = useState("");
  const query = useQuery({
    queryKey: ["batch", id],
    queryFn: async () => (await api<BatchDetails>(`/batches/${id}`)).data
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["batch", id] }),
      queryClient.invalidateQueries({ queryKey: ["batches"] })
    ]);
  };
  const allocate = useMutation({
    mutationFn: (version: number) =>
      api(`/batches/${id}/allocate`, { method: "POST", body: { expectedVersion: version } }),
    onSuccess: refresh
  });
  const start = useMutation({
    mutationFn: (version: number) =>
      api(`/batches/${id}/start`, { method: "POST", body: { expectedVersion: version } }),
    onSuccess: refresh
  });
  const deliverBatch = useMutation({
    mutationFn: (version: number) =>
      api<{ deliveredCount: number; noShowCount: number }>(`/batches/${id}/deliver`, {
        method: "POST",
        body: { expectedVersion: version },
        idempotencyKey: newIdempotencyKey()
      }),
    onSuccess: async (response) => {
      setDeliveryNotice(
        `تم تسليم ${response.data.deliveredCount} تخصيصًا وإغلاق الدفعة، مع استثناء ${response.data.noShowCount} حالة عدم حضور.`
      );
      setDeliveryConfirmOpen(false);
      await refresh();
    }
  });
  const noShow = useMutation({
    mutationFn: (row: Allocation) =>
      api(`/allocations/${row.id}/no-show`, {
        method: "POST",
        body: { expectedAllocationVersion: row.version, reason: noShowReason }
      }),
    onSuccess: async () => {
      setNoShowTarget(null);
      setNoShowReason("");
      await refresh();
    }
  });
  const cancel = useMutation({
    mutationFn: (version: number) =>
      api(`/batches/${id}/cancel`, {
        method: "POST",
        body: { expectedVersion: version, reason: cancelReason }
      }),
    onSuccess: async () => {
      setCancelOpen(false);
      setCancelReason("");
      await refresh();
    }
  });
  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  const batch = query.data;
  const actionError = allocate.error ?? start.error ?? deliverBatch.error ?? noShow.error ?? cancel.error;
  return (
    <div className="page">
      <Link className="back-link" to="/batches">
        <ArrowRight aria-hidden="true" />
        العودة إلى الدفعات
      </Link>
      <PageHeader
        title={`الدفعة ${batch.batch_number}`}
        description={`${batch.agent_name} · ${batch.scope_name} · ${formatDate(batch.scheduled_date)}`}
        actions={
          <div className="action-row">
            <StatusBadge status={batch.status} />
            {batch.allocations.length > 0 ? (
              <button className="button button--secondary" type="button" onClick={() => window.print()}>
                <Printer aria-hidden="true" /> طباعة الكشف
              </button>
            ) : null}
            {batch.status === "draft" && can("distribution.edit") ? (
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setEditOpen((value) => !value)}
              >
                <Pencil aria-hidden="true" /> تعديل
              </button>
            ) : null}
            {batch.status === "draft" && can("distribution.allocate") ? (
              <button
                className="button"
                type="button"
                disabled={allocate.isPending}
                onClick={() => allocate.mutate(batch.version)}
              >
                <LockKeyhole aria-hidden="true" />
                {allocate.isPending ? "جارٍ التخصيص…" : "تخصيص واعتماد"}
              </button>
            ) : null}
            {batch.status === "ready" && can("distribution.edit") ? (
              <button
                className="button"
                type="button"
                disabled={start.isPending}
                onClick={() => start.mutate(batch.version)}
              >
                <Play aria-hidden="true" /> بدء التوزيع
              </button>
            ) : null}
            {["ready", "in_progress"].includes(batch.status) && can("distribution.deliver") ? (
              <button
                className="button"
                type="button"
                disabled={deliverBatch.isPending}
                onClick={() => setDeliveryConfirmOpen(true)}
              >
                <PackageCheck aria-hidden="true" /> تسليم الدفعة
              </button>
            ) : null}
            {["draft", "ready", "in_progress"].includes(batch.status) && can("distribution.cancel") ? (
              <button className="button button--secondary" type="button" onClick={() => setCancelOpen(true)}>
                <Ban aria-hidden="true" /> إلغاء
              </button>
            ) : null}
          </div>
        }
      />
      {actionError ? (
        <div className="inline-alert" role="alert">
          {formatApiError(actionError)}
        </div>
      ) : null}
      {deliveryNotice ? (
        <div className="success-panel" role="status">
          <PackageCheck aria-hidden="true" /> <p>{deliveryNotice}</p>
        </div>
      ) : null}
      {deliveryConfirmOpen ? (
        <section
          className="content-card confirmation-panel no-print"
          aria-labelledby="detail-delivery-confirm-title"
        >
          <div>
            <h2 id="detail-delivery-confirm-title">تأكيد تسليم الدفعة كاملة</h2>
            <p>
              سيسجل النظام جميع التخصيصات القائمة كتسليم في عملية ذرية واحدة، ثم يغلق الدفعة. حالات عدم الحضور
              المسجلة لن تُحسب كتسليم.
            </p>
          </div>
          <div className="dialog-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setDeliveryConfirmOpen(false)}
            >
              تراجع
            </button>
            <button
              className="button"
              type="button"
              disabled={deliverBatch.isPending}
              onClick={() => deliverBatch.mutate(batch.version)}
            >
              {deliverBatch.isPending ? "جارٍ التنفيذ…" : "تأكيد تسليم الدفعة"}
            </button>
          </div>
        </section>
      ) : null}
      {noShowTarget ? (
        <section className="content-card management-form no-print" aria-labelledby="no-show-title">
          <h2 id="no-show-title">تسجيل عدم حضور: {noShowTarget.head_name}</h2>
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              noShow.mutate(noShowTarget);
            }}
          >
            <Field label="سبب عدم الحضور" htmlFor="no-show-reason">
              <textarea
                id="no-show-reason"
                minLength={3}
                value={noShowReason}
                onChange={(event) => setNoShowReason(event.target.value)}
                required
              />
            </Field>
            <div className="dialog-actions form-span">
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setNoShowTarget(null)}
              >
                تراجع
              </button>
              <button className="button" type="submit" disabled={noShow.isPending}>
                تأكيد عدم الحضور
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {cancelOpen ? (
        <section className="content-card management-form" aria-labelledby="cancel-title">
          <h2 id="cancel-title">إلغاء الدفعة</h2>
          <p>لن تُحذف الدفعة؛ تُحرر التخصيصات غير المسلّمة ويُحفظ السبب في التدقيق.</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              cancel.mutate(batch.version);
            }}
            className="form-grid"
          >
            <Field label="سبب الإلغاء" htmlFor="cancel-reason">
              <textarea
                id="cancel-reason"
                minLength={5}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                required
              />
            </Field>
            <div className="dialog-actions">
              <button className="button button--secondary" type="button" onClick={() => setCancelOpen(false)}>
                تراجع
              </button>
              <button className="button" type="submit" disabled={cancel.isPending}>
                تأكيد الإلغاء
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {editOpen ? (
        <BatchEditForm
          batch={batch}
          onSaved={async () => {
            setEditOpen(false);
            await refresh();
          }}
          onCancel={() => setEditOpen(false)}
        />
      ) : null}
      <section className="content-card batch-summary batch-manifest" id="batch-manifest">
        <div className="print-only manifest-title">
          <h1>كشف الدفعة {batch.batch_number}</h1>
          <p>
            {batch.agent_name} · {batch.scope_name} · {formatDate(batch.scheduled_date)}
          </p>
        </div>
        <dl className="definition-grid">
          <div>
            <dt>السعة</dt>
            <dd>{formatNumber(batch.quantity)} أسطوانة</dd>
          </div>
          <div>
            <dt>دورة الاستحقاق</dt>
            <dd>{batch.cycle_name ?? "غير مرتبطة"}</dd>
          </div>
          <div>
            <dt>التخصيصات</dt>
            <dd>{formatNumber(batch.allocations.length)}</dd>
          </div>
          <div>
            <dt>بصمة التخصيص</dt>
            <dd dir="ltr">{batch.allocation_digest ? `${batch.allocation_digest.slice(0, 18)}…` : "—"}</dd>
          </div>
        </dl>
        {batch.notes ? (
          <div className="note-box">
            <strong>ملاحظات</strong>
            <p>{batch.notes}</p>
          </div>
        ) : null}
      </section>
      <section className="content-card batch-manifest">
        <div className="section-head">
          <div>
            <h2>قائمة المستفيدين</h2>
            <p>{formatNumber(batch.allocations.length)} تخصيصًا مرتبًا وفق محرك الاستحقاق.</p>
          </div>
        </div>
        {batch.allocations.length === 0 ? (
          <EmptyState
            title="لم تُخصّص الدفعة بعد"
            description="عند الاعتماد، سيختار الخادم الأسر المستحقة ويقفل بصمة النتيجة."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الترتيب</th>
                  <th>رقم الأسرة</th>
                  <th>رب الأسرة</th>
                  <th>الهاتف</th>
                  <th>الحالة</th>
                  {can("distribution.deliver") ? <th className="no-print">إجراء</th> : null}
                </tr>
              </thead>
              <tbody>
                {batch.allocations.map((row) => (
                  <tr key={row.id}>
                    <td>{formatNumber(row.rank_in_batch)}</td>
                    <td>
                      <Link className="row-title" to={`/households/${row.household_id}`}>
                        {row.household_code}
                      </Link>
                    </td>
                    <td>{row.head_name}</td>
                    <td dir="ltr">•••• {row.phone_last4 ?? "—"}</td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                    {can("distribution.deliver") ? (
                      <td className="no-print">
                        {row.status === "allocated" ? (
                          <button
                            className="button button--ghost"
                            type="button"
                            aria-label={`تسجيل عدم حضور ${row.head_name}`}
                            onClick={() => setNoShowTarget(row)}
                          >
                            <UserX aria-hidden="true" /> عدم حضور
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function BatchEditForm({
  batch,
  onSaved,
  onCancel
}: {
  batch: BatchDetails;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [agentId, setAgentId] = useState(batch.agent_id);
  const [scopeUnitId, setScopeUnitId] = useState(batch.scope_unit_id);
  const [cycleId, setCycleId] = useState(batch.cycle_id ?? "");
  const [scheduledDate, setScheduledDate] = useState(batch.scheduled_date);
  const [quantity, setQuantity] = useState(String(batch.quantity));
  const [notes, setNotes] = useState(batch.notes ?? "");
  const agents = useQuery({
    queryKey: ["agents", "active-options"],
    queryFn: async () => (await api<AgentOption[]>("/agents?limit=100&status=active")).data
  });
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data
  });
  const cycles = useQuery({
    queryKey: ["cycles"],
    queryFn: async () => (await api<CycleOption[]>("/cycles")).data
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/batches/${batch.id}`, {
        method: "PATCH",
        body: {
          agentId,
          scopeUnitId,
          cycleId: cycleId || undefined,
          scheduledDate,
          quantity: Number(quantity),
          notes,
          expectedVersion: batch.version
        }
      }),
    onSuccess: onSaved
  });
  useEffect(() => {
    if (!cycleId && batch.cycle_id) setCycleId(batch.cycle_id);
  }, [batch.cycle_id, cycleId]);
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <section className="content-card management-form" aria-labelledby="edit-batch-title">
      <h2 id="edit-batch-title">تعديل المسودة</h2>
      <form className="form-grid" onSubmit={submit}>
        <Field label="الوكيل" htmlFor="edit-batch-agent">
          <select id="edit-batch-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
            {(agents.data ?? []).map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="النطاق" htmlFor="edit-batch-scope">
          <select
            id="edit-batch-scope"
            value={scopeUnitId}
            onChange={(e) => setScopeUnitId(e.target.value)}
            required
          >
            {(units.data ?? [])
              .filter((unit) => unit.status === "active")
              .map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name_ar}
                </option>
              ))}
          </select>
        </Field>
        <Field label="الدورة" htmlFor="edit-batch-cycle">
          <select id="edit-batch-cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
            <option value="">بدون دورة</option>
            {(cycles.data ?? [])
              .filter((cycle) => cycle.status === "locked")
              .map((cycle) => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name_ar}
                </option>
              ))}
          </select>
        </Field>
        <Field label="التاريخ" htmlFor="edit-batch-date">
          <input
            id="edit-batch-date"
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            required
          />
        </Field>
        <Field label="الكمية" htmlFor="edit-batch-quantity">
          <input
            id="edit-batch-quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
          />
        </Field>
        <Field label="الملاحظات" htmlFor="edit-batch-notes">
          <textarea id="edit-batch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {save.isError ? (
          <div className="inline-alert form-span" role="alert">
            {formatApiError(save.error)}
          </div>
        ) : null}
        <div className="dialog-actions form-span">
          <button className="button button--secondary" type="button" onClick={onCancel}>
            إلغاء
          </button>
          <button className="button" type="submit" disabled={save.isPending}>
            <Save aria-hidden="true" /> حفظ المسودة
          </button>
        </div>
      </form>
    </section>
  );
}
